import { useState, useCallback, useEffect, useRef } from 'react';
import type { TourStep } from '@/components/onboarding/tour/types';

interface UseTourOptions {
  steps: TourStep[];
  onComplete: () => void;
  onSkip: () => void;
  initialStep?: number;
}

export function useTour({ steps, onComplete, onSkip, initialStep = 0 }: UseTourOptions) {
  const [currentIndex, setCurrentIndex] = useState(initialStep);
  const [isActive, setIsActive] = useState(false);
  const [direction, setDirection] = useState(1);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const currentStep = isActive ? steps[currentIndex] ?? null : null;

  const start = useCallback((fromStep = 0) => {
    setCurrentIndex(fromStep);
    setIsActive(true);
    setDirection(1);

    // Call beforeShow on first step
    const step = stepsRef.current[fromStep];
    if (step?.beforeShow) {
      step.beforeShow();
    }
  }, []);

  const next = useCallback(async () => {
    const current = stepsRef.current[currentIndex];

    // Call afterHide on current step
    current?.afterHide?.();

    if (currentIndex >= stepsRef.current.length - 1) {
      // Last step - complete
      setIsActive(false);
      onComplete();
      return;
    }

    const nextIndex = currentIndex + 1;
    const nextStep = stepsRef.current[nextIndex];

    // Call beforeShow on next step
    if (nextStep?.beforeShow) {
      await nextStep.beforeShow();
    }

    setDirection(1);
    setCurrentIndex(nextIndex);
  }, [currentIndex, onComplete]);

  const prev = useCallback(async () => {
    if (currentIndex <= 0) return;

    const current = stepsRef.current[currentIndex];
    current?.afterHide?.();

    const prevIndex = currentIndex - 1;
    const prevStep = stepsRef.current[prevIndex];

    if (prevStep?.beforeShow) {
      await prevStep.beforeShow();
    }

    setDirection(-1);
    setCurrentIndex(prevIndex);
  }, [currentIndex]);

  const skip = useCallback(() => {
    const current = stepsRef.current[currentIndex];
    current?.afterHide?.();
    setIsActive(false);
    onSkip();
  }, [currentIndex, onSkip]);

  const goTo = useCallback(async (index: number) => {
    if (index < 0 || index >= stepsRef.current.length) return;

    const current = stepsRef.current[currentIndex];
    current?.afterHide?.();

    const target = stepsRef.current[index];
    if (target?.beforeShow) {
      await target.beforeShow();
    }

    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          skip();
          break;
        case 'ArrowRight':
          e.preventDefault();
          next();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prev();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, next, prev, skip]);

  return {
    currentStep,
    currentIndex,
    totalSteps: steps.length,
    isActive,
    direction,
    next,
    prev,
    skip,
    goTo,
    start,
  };
}
